import React, { useContext } from 'react';
import { Stack } from '@mui/material';

import { AppContext } from 'context/app';
import { CameraStateContext } from 'context/state/camera';

import { Fullscreen } from 'components/camera/controls/Fullscreen';
import { Microphone } from 'components/camera/controls/Microphone';
import { MotionTracking } from 'components/camera/controls/MotionTracking';
import { NightVision } from 'components/camera/controls/NightVision';
import { PictureInPicture } from 'components/camera/controls/PictureInPicture';
import { Power } from 'components/camera/controls/Power';
import { Record } from 'components/camera/controls/Record';
import { Screenshot } from 'components/camera/controls/Screenshot';
import { Siren } from 'components/camera/controls/Siren';
import { Sound } from 'components/camera/controls/Sound';
import { isCommandSupported } from 'modules/aspenCommands';
import { isSettingSupported } from 'modules/aspenSettings';
import { isWyze } from 'modules/leaf';

import { AppDeviceLeaf } from 'types/common/leaf';

import styles from './cameraControlDeck.module.scss';

export type CameraControlDeckType = 'compact' | 'normal';

interface CameraControlDeckProps {
  leaf: AppDeviceLeaf;
  controlDeckType?: CameraControlDeckType;
}

export const CameraControlDeck = ({ leaf, controlDeckType = 'normal' }: CameraControlDeckProps) => {
  const { fullscreenEnabled, microphoneEnabled, pipEnabled, soundEnabled, recording } = useContext(CameraStateContext);
  const { microphonePermissionState: micState } = useContext(AppContext);
  const microphonePermissionState = isWyze(leaf) ? 'wyze' : micState;

  if (fullscreenEnabled || controlDeckType === 'normal') {
    return (
      <Stack direction="row" className={styles.controlDeck} data-testid="component-camera-control-deck-normal">
        <div id={`${leaf.name}-left-deck`}>
          <PictureInPicture leafId={leaf.id} pipEnabled={pipEnabled} />
        </div>
        <Sound leafId={leaf.id} soundEnabled={soundEnabled} />
        <Record leafId={leaf.id} recording={recording} />
        <Microphone
          leafId={leaf.id}
          microphonePermissionState={microphonePermissionState}
          microphoneEnabled={microphoneEnabled}
        />
        <Screenshot leafId={leaf.id} />
        {isSettingSupported(leaf.supportedSettings, 'nightVisionMode') && <NightVision leaf={leaf} />}
        {isSettingSupported(leaf.supportedSettings, 'motionTracking') && <MotionTracking leaf={leaf} />}
        {isCommandSupported(leaf.supportedCommands, 'siren') && <Siren leaf={leaf} />}
        {isCommandSupported(leaf.supportedCommands, 'power') && <Power leaf={leaf} />}
        <div id={`${leaf.name}-right-deck`}>
          <Fullscreen leafId={leaf.id} fullscreenEnabled={fullscreenEnabled} />
        </div>
      </Stack>
    );
  }

  if (controlDeckType === 'compact') {
    return (
      <Stack direction="row" className={styles.controlDeck} data-testid="component-camera-control-deck-compact">
        <div id={`${leaf.name}-left-deck`}>
          <PictureInPicture leafId={leaf.id} pipEnabled={pipEnabled} />
        </div>
        <Record leafId={leaf.id} recording={recording} />
        <Microphone
          leafId={leaf.id}
          microphonePermissionState={microphonePermissionState}
          microphoneEnabled={microphoneEnabled}
        />
        <Screenshot leafId={leaf.id} />
        <div id={`${leaf.name}-right-deck`}>
          <Fullscreen leafId={leaf.id} fullscreenEnabled={fullscreenEnabled} />
        </div>
      </Stack>
    );
  }
  return null;
};
